/**
 * SOLID Review
 * S — Strong. One concern: the game screen's ad area — whether to ask for this player's ad config, keeping the
 *     answer, and which zone the area shows.
 * O — Good. A new provider needs no change; a new zone is one more branch in `zone` and one value of `TClientAdZone`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. The session part a decision reads, two moves and the screen's one question.
 * D — Strong. The schemas' types and the builder's part role.
 */
import type { IStateClientSessionPart } from '../decisions/ClientDecision';
import type { IStateClientSession, IPropsClientSessionChanges, IEntityClientPlayer } from '../ports/schemas/ClientSession';
import type { IEntityClientConsent } from '../ports/schemas/ConsentMoves';
import {
  NO_ADS, type IEntityClientAds, type IPayloadAdConfigRequest, type ICommandAdConfigLoaded, type TClientAdZone,
} from '../ports/schemas/AdsMoves';

/**
 * THE GAME SCREEN'S AD AREA, AS A SMALL AGGREGATE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide
 * functions of FE4d (`decisions/ads/adSlot.ts`, useAdProviderConfig's, moved) and AppRoot's zone ternary
 * (`ads/adZone.ts`'s `adZoneOf`, 2026-10-04), one object over the session's player, consent and ad config.
 *
 * THE ZONE IS READ WHEN THE VIEW IS BUILT, NOT KEPT (deviation from the strict-ports plan's "settleSession
 * recomputes", flagged in §28.201): a table decision's reset unseats the player without a session step, and a kept
 * zone would then be stale.
 */
export class AdSlot implements IStateClientSessionPart {
  private constructor(
    private readonly player: IEntityClientPlayer | null,
    private readonly consent: IEntityClientConsent,
    private ads: IEntityClientAds,
  ) {}

  /** The ad area of the session as it stands. */
  static of(session: Pick<IStateClientSession, 'player' | 'consent' | 'ads'>): AdSlot {
    return new AdSlot(session.player, session.consent, session.ads);
  }

  /**
   * The request for the seated player's ad config, or none: a free player, seated, not yet asked for. Asking is marked
   * BEFORE the call, as the hook's ref was, so a failed request is not repeated for the same player; a new player
   * starts from the defaults.
   */
  configAsked(): IPayloadAdConfigRequest | null {
    const { player } = this;
    if (player === null || player.matchCredits > 0 || this.ads.playerId === player.playerId) return null;
    this.ads = { ...NO_ADS, playerId: player.playerId };
    const request: IPayloadAdConfigRequest = { playerId: player.playerId };
    return request;
  }

  /** The server's answer for a player: kept if it is still the player asked for; none keeps the defaults. */
  configLoaded(command: ICommandAdConfigLoaded): boolean {
    if (command.config === null || this.ads.playerId !== command.playerId) return false;
    this.ads = { ...command.config, playerId: command.playerId };
    return true;
  }

  /**
   * Which zone the area shows. A premium player has no ad area. Without advertising consent (denied, or not yet
   * resolved) the same-sized area holds a cookie-free placeholder, so nothing shifts and no ad or cookie is served.
   * With it: AdSense's slot, or Monetag's banner.
   */
  zone(): TClientAdZone {
    if (this.player !== null && this.player.matchCredits > 0) return 'none';
    if (!this.consent.advertisingAllowed) return 'placeholder';
    return this.ads.provider === 'ADSENSE' ? 'adsense' : 'propeller';
  }

  sessionFields(): IPropsClientSessionChanges {
    const fields: IPropsClientSessionChanges = { ads: this.ads };
    return fields;
  }
}
